import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import ResumeForm from "@/components/dashboard/ResumeForm";
import SavedResumes from "@/components/dashboard/SavedResumes";
import CreditManagement from "@/components/dashboard/CreditManagement";
import { useCredits } from "@/hooks/useCredits";
import { useTransactions, CreditTransaction } from "@/hooks/useTransactions";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("new");
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { credits, updateCredits } = useCredits();
  const { data: transactions = [] } = useTransactions();

  const handleUseCredit = async () => {
    if (credits <= 0) return;
    
    setIsProcessing(true);
    
    try {
      // Deduct credit
      await updateCredits.mutateAsync(credits - 1);
      
      // Record transaction
      await supabase.from("credit_transactions").insert({
        user_id: user?.id,
        type: "usage",
        amount: 1,
        description: 'Used for "Software Engineer - Google"',
      });
      
      toast({
        title: "Resume Optimized",
        description: "Your resume has been optimized using 1 credit!",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDirectPayment = () => {
    setIsProcessing(true);
    
    // Process with direct payment
    setTimeout(() => {
      setIsProcessing(false);
      toast({
        title: "Resume Optimized",
        description: "Your resume has been optimized after successful payment!",
      });
    }, 2000);
  };

  const handlePurchaseCredits = async (amount: number) => {
    try {
      // Add credits
      await updateCredits.mutateAsync(credits + amount);
      
      // Record transaction
      await supabase.from("credit_transactions").insert({
        user_id: user?.id,
        type: "purchase",
        amount: amount,
        description: `Purchased ${amount} credits`,
      });
      
      toast({
        title: "Credits Purchased",
        description: `Successfully purchased ${amount} credits!`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      
      <main className="flex-grow py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold">Dashboard</h1>
            <p className="text-gray-600">Manage and optimize your resumes</p>
          </div>
          
          <Tabs defaultValue="new" value={activeTab} onValueChange={setActiveTab} className="space-y-8">
            <TabsList className="grid w-full max-w-md grid-cols-3">
              <TabsTrigger value="new">Create New</TabsTrigger>
              <TabsTrigger value="saved">Saved Resumes</TabsTrigger>
              <TabsTrigger value="credits">Credits</TabsTrigger>
            </TabsList>
            
            <TabsContent value="new">
              <ResumeForm onTabChange={setActiveTab} />
            </TabsContent>
            
            <TabsContent value="saved">
              <SavedResumes onNewResume={() => setActiveTab("new")} />
            </TabsContent>
            
            <TabsContent value="credits">
              <CreditManagement 
                credits={credits}
                transactions={transactions as CreditTransaction[]}
                onPurchase={handlePurchaseCredits}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Dashboard;
